import { Monitor, TrendingUp, FlaskConical } from 'lucide-react';
import useInView from '../hooks/useInView';

const INDUSTRIES = [
  {
    icon: Monitor,
    title: 'Internet-First Product Companies',
    description: 'Scaling product and engineering teams at high-growth SaaS, consumer internet, and B2B software companies that move fast and hire smart.',
    examples: ['Product Management', 'Engineering', 'Growth & GTM'],
    image: 'https://images.unsplash.com/photo-1623479322729-28b25c16b011?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    icon: TrendingUp,
    title: 'Fintech & BFSI',
    description: 'Specialized hiring for payments, lending, insurtech, and digital banking teams. We understand the compliance-driven, execution-heavy landscape of financial services.',
    examples: ['Risk & Compliance', 'Product & Tech', 'Operations'],
    image: 'https://images.unsplash.com/photo-1643962577481-4ff81600e439?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    icon: FlaskConical,
    title: 'Life Sciences & Biotech',
    description: 'Supporting pharma, biotech, and medical device companies with roles that require both scientific depth and commercial acumen.',
    examples: ['Clinical & Regulatory', 'Commercial', 'R&D Leadership'],
    image: 'https://images.unsplash.com/photo-1631816290764-25bbf9ea8e85?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
];

export default function IndustriesSection() {
  const [ref, inView] = useInView();

  return (
    <section id="industries" className="py-24 bg-[#0A1628]" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#E8500A]" />
              <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
                Industries
              </span>
            </div>
            <h2
              className="text-4xl sm:text-5xl font-black text-white leading-none tracking-tight"
              style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              data-testid="industries-heading"
            >
              Deep Expertise<br />Where It Counts.
            </h2>
          </div>
          <div className="lg:pt-8">
            <p className="text-white/50 text-base leading-relaxed" style={{ fontFamily: '"Inter",sans-serif' }}>
              We don't hire for every industry. We hire exceptionally well in three. That focus is what lets us deliver speed, quality, and genuine market intelligence.
            </p>
          </div>
        </div>

        {/* Industry cards */}
        <div ref={ref} className="grid md:grid-cols-3 gap-4">
          {INDUSTRIES.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <div
                key={i}
                className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(24px)',
                  transition: `all 0.65s ease ${i * 150}ms`,
                  background: '#0d1e35',
                }}
                data-testid={`industry-card-${i}`}
              >
                {/* Image — prominently visible at top, fades to navy at bottom */}
                <div className="absolute inset-0">
                  <img
                    src={ind.image}
                    alt=""
                    className="w-full h-full object-cover object-center opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                    aria-hidden="true"
                  />
                  {/* Gradient: transparent top → heavy navy from midpoint down (for text legibility) */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-[#0A1628]/70 to-[#0A1628]" />
                </div>

                {/* Content */}
                <div className="relative p-8 lg:p-10 min-h-[360px] flex flex-col">
                  {/* Icon */}
                  <div className="w-11 h-11 flex items-center justify-center border border-[#E8500A]/50 bg-[#E8500A]/10 mb-auto rounded-sm group-hover:bg-[#E8500A]/20 transition-colors duration-300 flex-shrink-0">
                    <Icon size={19} className="text-[#E8500A]" />
                  </div>

                  {/* Push text content to bottom */}
                  <div className="mt-auto pt-32">
                    <h3
                      className="text-xl font-black text-white mb-3 leading-tight"
                      style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                    >
                      {ind.title}
                    </h3>
                    <p className="text-sm text-white/65 leading-relaxed mb-5" style={{ fontFamily: '"Inter",sans-serif' }}>
                      {ind.description}
                    </p>

                    <div className="space-y-2">
                      {ind.examples.map((ex, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-[#E8500A] rounded-full flex-shrink-0" />
                          <span className="text-xs text-white/50 font-medium" style={{ fontFamily: '"Inter",sans-serif' }}>{ex}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E8500A] group-hover:w-full transition-all duration-500" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
